import { gameState } from '../../game/state.js';

export default class CollisionHandler {
  constructor(scene) {
    this.scene = scene;
  }

  setupCollisions() {
    const player = this.scene.getPlayer();
    const physicsGroups = this.scene.getPhysicsGroups();
    
    // Player collisions with collectibles (INCLUDES QUESTION BOXES)
    this.scene.physics.add.overlap(player, physicsGroups.collectibles, this.collectItem, null, this);
    
    // Player collisions with obstacles
    this.scene.physics.add.overlap(player, physicsGroups.obstacles, this.hitObstacle, null, this);
    
    // Player collisions with NPCs
    this.scene.physics.add.overlap(player, physicsGroups.npcs, this.interactWithNPC, null, this);
    
    // Player collisions with platforms
    this.scene.physics.add.collider(player, physicsGroups.platforms);
    
    /* console.log('💥 Collision system setup - question boxes in collectibles'); */
  }

  // MAIN COLLECTION METHOD - handles both regular items and question boxes
  collectItem(player, item) {
    /* console.log('🔍 collectItem called with:', item.itemType || 'unknown', 'isQuestionBox:', item.isQuestionBox); */
    
    // Check if it's a question box FIRST
    if (item.isQuestionBox) {
      /* console.log('📦 QUESTION BOX DETECTED!'); */
      this.triggerQuestionBox(player, item);
      return;
    }
    
    // Regular item collection
    /* console.log('💰 Processing regular item:', item.itemType); */
    const points = gameState.collectItem(item.points, item.itemType);
    
    // Visual effect
    this.scene.getEffectsManager().createCollectionEffect(item.x, item.y, points);
    
    // Show points UI
    this.scene.uiManager.showPointsChange(item.x, item.y - 30, points, true);
    
    // Remove item
    item.destroy();
    
    /* console.log(`💰 Collected ${item.itemType}! +${points} points`); */
  }

  triggerQuestionBox(player, qBox) {
    if (qBox.isUsed) {
      /* console.log('📦 Question box already used, ignoring...'); */
      return;
    }
    
    /* console.log('📦 Triggering question box effects...'); */
    qBox.isUsed = true;
    
    // Create the bust effect
    this.createQuestionBoxBustEffect(qBox);
    
    // Give random reward
    const rewards = [
      { type: 'points', value: 500 },
      { type: 'points', value: 1000 },
      { type: 'items', value: 3 }
    ];
    
    const reward = Phaser.Utils.Array.GetRandom(rewards);
    
    // Delay reward so effect shows first
    this.scene.time.delayedCall(300, () => {
      this.giveQuestionBoxReward(qBox, reward);
    });
  }

  createQuestionBoxBustEffect(qBox) {
    const x = qBox.x;
    const y = qBox.y;
    
    /* console.log('💥 Creating bust effect at', x, y); */
    
    // 1. BOX BOUNCE
    this.scene.tweens.add({
      targets: qBox,
      y: y - 15,
      duration: 150,
      yoyo: true,
      onComplete: () => {
        qBox.setTint(0x8B4513); // Turn brown (empty)
        this.scene.tweens.killTweensOf(qBox);
      }
    });
    
    // 2. FLYING QUESTION MARK
    const questionMark = this.scene.add.text(x, y, '?', {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: questionMark,
      y: y - 80,
      x: x + 20,
      alpha: 0,
      rotation: Math.PI * 2,
      duration: 1500,
      onComplete: () => questionMark.destroy()
    });
    
    // 3. GOLDEN EXPLOSION
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const particle = this.scene.add.circle(x, y, 6, 0xFFD700);
      
      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * 60,
        y: y + Math.sin(angle) * 60,
        alpha: 0,
        duration: 800,
        onComplete: () => particle.destroy()
      });
    }
    
    // 4. SCREEN SHAKE
    this.scene.cameras.main.shake(200, 0.01);
    
    /* console.log('💥 Bust effect created!'); */
  }

  giveQuestionBoxReward(qBox, reward) {
    const x = qBox.x;
    const y = qBox.y;
    
    if (reward.type === 'points') {
      gameState.collectItem(reward.value, 'bonus');
      this.scene.uiManager.showPointsChange(x, y - 30, reward.value, true);
      
      // Coin effects
      for (let i = 0; i < 5; i++) {
        const coin = this.scene.add.circle(x, y, 8, 0xFFD700);
        this.scene.tweens.add({
          targets: coin,
          x: x + (Math.random() - 0.5) * 100,
          y: y - 50,
          alpha: 0,
          duration: 1000,
          onComplete: () => coin.destroy()
        });
      }
    }
    
    /* console.log('🎁 Reward given:', reward.value, 'points'); */
  }

  hitObstacle(player, obstacle) {
    if (player.isInvincible) {
      // If player is invincible, destroy obstacle instead
      this.scene.getEffectsManager().createExplosionEffect(obstacle.x, obstacle.y);
      obstacle.destroy();
      return;
    }
    
    // Take damage
    gameState.takeDamage();
    
    // Damage effects
    this.scene.getEffectsManager().createDamageEffect(player.x, player.y);
    
    // Show point loss
    const pointsLost = 50;
    this.scene.uiManager.showPointsChange(player.x, player.y - 30, pointsLost, false);
    
    // Remove obstacle
    obstacle.destroy();
    
    /* console.log('💥 Hit obstacle!'); */
  }

  interactWithNPC(player, npc) {
    
    // Check if player is invincible
    if (player.isInvincible) {
      console.log('🛡️ Player is invincible, no damage taken');
      return;
    }
    
    // Take damage
    const damageResult = gameState.takeDamage();
    
    // Create damage effect
    this.scene.getEffectsManager().createDamageEffect(player.x, player.y);
    
    // Show point loss
    const pointsLost = 50;
    this.scene.uiManager.showPointsChange(npc.x, npc.y - 30, pointsLost, false);
    
    // Create NPC interaction effect
    this.scene.getEffectsManager().createNPCInteractionEffect(npc.x, npc.y, npc.npcType);
    
    // Remove NPC
    npc.destroy();

  }


  // Check if player is touching ground or platforms
  checkGroundContact(player) {
    const physicsGroups = this.scene.getPhysicsGroups();
    
    // Check ground contact
    const groundContact = this.scene.physics.overlap(player, physicsGroups.ground);
    
    // Check platform contact
    const platformContact = this.scene.physics.overlap(player, physicsGroups.platforms);
    
    return groundContact || platformContact || player.body.touching.down || player.body.blocked.down;
  }

  // Manual collision check for specific objects
  checkCollision(object1, object2) {
    return this.scene.physics.overlap(object1, object2);
  }

  // Get all objects colliding with a specific object
  getCollidingObjects(targetObject, group) {
    const colliding = [];
    
    group.children.entries.forEach(object => {
      if (this.scene.physics.overlap(targetObject, object)) {
        colliding.push(object);
      }
    });
    
    return colliding;
  }



  // Keep the old method name for compatibility but redirect to new one
  hitHeartBox(player, heartBox) {
    /* console.log('🔍 Redirecting to question box handler...'); */
    this.hitQuestionBox(player, heartBox);
  }

  collectItem(player, item) {
    // Award points
    const points = gameState.collectItem(item.points, item.itemType);
    
    // Visual effect
    this.scene.getEffectsManager().createCollectionEffect(item.x, item.y, points);
    
    // Show points UI
    this.scene.uiManager.showPointsChange(item.x, item.y - 30, points, true);
    
    // Remove item
    item.destroy();
    
    /* console.log(`💰 Collected ${item.itemType}! +${points} points`); */
  }

  hitObstacle(player, obstacle) {
    if (player.isInvincible) {
      // If player is invincible, destroy obstacle instead
      this.scene.getEffectsManager().createExplosionEffect(obstacle.x, obstacle.y);
      obstacle.destroy();
      return;
    }
    
    // Take damage
    gameState.takeDamage();
    
    // Damage effects
    this.scene.getEffectsManager().createDamageEffect(player.x, player.y);
    
    // Show point loss
    const pointsLost = 50;
    this.scene.uiManager.showPointsChange(player.x, player.y - 30, pointsLost, false);
    
    // Remove obstacle
    obstacle.destroy();
    
    /* console.log('💥 Hit obstacle!'); */
  }

  interactWithNPC(player, npc) {
    // NPCs now cause damage instead of giving bonuses
    gameState.takeDamage();
    
    // Create damage effect
    this.scene.getEffectsManager().createDamageEffect(player.x, player.y);
    
    // Show point loss
    const pointsLost = 50;
    this.scene.uiManager.showPointsChange(npc.x, npc.y - 30, pointsLost, false);
    
    // Create NPC interaction effect
    this.scene.getEffectsManager().createNPCInteractionEffect(npc.x, npc.y, npc.npcType);
    
    // Remove NPC
    npc.destroy();
    /* console.log(`💥 Hit ${npc.npcType}! Lost points!`); */
  }

  hitHeartBox(player, heartBox) {
    // Check if it's a question box and not already used
    if (heartBox.boxType === 'question' && !heartBox.isUsed) {
      console.log('📦 Question box hit! Triggering surprise...');
      
      // Mark as used
      heartBox.isUsed = true;
      
      // Change appearance to "empty" box
      heartBox.setTint(0x8B4513); // Brown/empty color
      
      // Stop animations
      this.scene.tweens.killTweensOf(heartBox);
      
      // Random reward
      const rewards = [
        { type: 'points', value: 500, message: 'BONUS POINTS!' },
        { type: 'points', value: 1000, message: 'JACKPOT!' },
        { type: 'items', value: 3, message: 'ITEM BURST!' },
        { type: 'ability', value: 1, message: 'ABILITY RESET!' }
      ];
      
      const reward = Phaser.Utils.Array.GetRandom(rewards);
      this.giveQuestionBoxReward(player, heartBox, reward);
      
      /* console.log(`📦 Question box reward: ${reward.type} - ${reward.message}`); */
      return;
    }
    
    // Original heart box logic for non-question boxes
    if (heartBox.boxType !== 'question') {
      /* console.log('💝 Heart box hit! Triggering bonus...'); */
      
      // Award bonus points
      const bonusPoints = 300;
      gameState.collectItem(bonusPoints, 'heart-bonus');
      
      // Create heart explosion effect
      this.scene.getEffectsManager().createHeartBoxEffect(heartBox.x, heartBox.y);
      
      // Show bonus points
      this.scene.uiManager.showPointsChange(heartBox.x, heartBox.y - 30, bonusPoints, true);
      
      // Remove heart box
      heartBox.destroy();
      
      /* console.log(`💖 Heart box collected! +${bonusPoints} points`); */
    }
  }

  giveQuestionBoxReward(player, box, reward) {
    const x = box.x;
    const y = box.y;
    
    switch (reward.type) {
      case 'points':
        // Award bonus points
        gameState.collectItem(reward.value, 'question-bonus');
        this.scene.uiManager.showPointsChange(x, y - 30, reward.value, true);
        
        // Gold coin effect
        for (let i = 0; i < 5; i++) {
          const coin = this.scene.add.circle(x, y, 4, 0xFFD700);
          this.scene.tweens.add({
            targets: coin,
            x: x + (Math.random() - 0.5) * 80,
            y: y - 50 - (Math.random() * 30),
            alpha: 0,
            duration: 1000,
            onComplete: () => coin.destroy()
          });
        }
        break;
        
      case 'items':
        // Spawn collectible items
        for (let i = 0; i < reward.value; i++) {
          this.scene.time.delayedCall(i * 200, () => {
            const itemTypes = ['ring', 'heart-coin', 'bouquet'];
            const itemType = Phaser.Utils.Array.GetRandom(itemTypes);
            
            const item = this.scene.physics.add.sprite(
              x + (Math.random() - 0.5) * 60,
              y - 20,
              itemType
            );
            item.setScale(2);
            item.body.setGravityY(0);
            item.body.setVelocity((Math.random() - 0.5) * 100, -150);
            item.itemType = itemType;
            item.points = 100;
            
            // Add to collectibles group
            this.scene.getPhysicsGroups().collectibles.add(item);
            
            // Auto cleanup
            this.scene.time.delayedCall(5000, () => {
              if (item && item.active) item.destroy();
            });
          });
        }
        this.scene.uiManager.showMessage(reward.message, x, y - 40, 2000, { color: '#32CD32' });
        break;
        
      case 'ability':
        // Reset ability cooldown
        gameState.abilityReady = true;
        gameState.abilityCooldown = 0;
        
        // Sparkle effect
        for (let i = 0; i < 8; i++) {
          const sparkle = this.scene.add.circle(x, y, 3, 0x00CED1);
          this.scene.tweens.add({
            targets: sparkle,
            x: x + (Math.random() - 0.5) * 100,
            y: y - 30 - (Math.random() * 50),
            alpha: 0,
            scale: 2,
            duration: 1500,
            onComplete: () => sparkle.destroy()
          });
        }
        this.scene.uiManager.showMessage(reward.message, x, y - 40, 2000, { color: '#00CED1' });
        break;
    }
    
    // Show reward message
    this.scene.uiManager.showMessage(reward.message, x, y - 60, 2000, { 
      fontSize: '14px', 
      color: '#FFD700' 
    });
  }

  // Check if player is touching ground or platforms
  checkGroundContact(player) {
    const physicsGroups = this.scene.getPhysicsGroups();
    
    // Check ground contact
    const groundContact = this.scene.physics.overlap(player, physicsGroups.ground);
    
    // Check platform contact
    const platformContact = this.scene.physics.overlap(player, physicsGroups.platforms);
    
    return groundContact || platformContact || player.body.touching.down || player.body.blocked.down;
  }

  // Manual collision check for specific objects
  checkCollision(object1, object2) {
    return this.scene.physics.overlap(object1, object2);
  }

  // Get all objects colliding with a specific object
  getCollidingObjects(targetObject, group) {
    const colliding = [];
    
    group.children.entries.forEach(object => {
      if (this.scene.physics.overlap(targetObject, object)) {
        colliding.push(object);
      }
    });
    
    return colliding;
  }
}